import { useGetRequestedLogisticsQuotesQuery } from "../../../store/apiSlices/logistics";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { calculatePriceRange, parseServerDate, timeAgo } from "../../../utils";
import { getRFQStatusStyles } from "../../Admin/CustomerRFQsDetails/RFQCard";
import {
  AirPlaneIcon,
  BoxIcon,
  CheckIcon,
  ClockIcon,
  CrossIcon,
  MapPinIcon,
  ShipIcon,
  SpinnerIcon,
} from "../../Icons";
import QuoteCard from "./QuoteCard";

export const OrderRequestKeys = [
  {
    label: "Goods Description",
    key: "goods_description",
  },
  {
    label: "Goods Specifications",
    key: "goods_specifications",
  },
  {
    label: "Procurement Location",
    key: "procurement_location",
  },
  {
    label: "Target Price",
    key: "target_price",
  },
  {
    label: "Additional Notes",
    key: "additional_notes",
  },
] as const;

export default function RFQs({ onSelect }: { onSelect: (item: any) => void }) {
  const { data, isLoading, isError, isFetching, refetch } =
    useGetRequestedLogisticsQuotesQuery();

  if (isLoading) return <RFQSkeletonList />;

  if (isError)
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <p className="text-sm text-gray-500">Failed to load RFQs.</p>

        <button
          onClick={refetch}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700
               disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetching && (
            <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-gray-600" />
          )}
          Try again
        </button>
      </div>
    );

  const rfqs =
    data?.filter((el) => !el.status || el.status.toLowerCase() === "pending") ||
    [];

  if (!rfqs.length)
    return <p className="text-center text-sm text-gray-400">No RFQs found</p>;

  return (
    <div className="space-y-3">
      {rfqs.map((rfq) =>
        rfq.booking_medium === "procurement_request" ? (
          <QuoteCard
            key={rfq.id || rfq.quotes_id}
            quote={rfq}
            onSelect={onSelect}
          >
            <QuoteCard.Header>
              <div className="flex items-center gap-2 text-gray-700">
                <MapPinIcon className="size-4 text-gray-500" />
                <h3 className="text-sm font-semibold">
                  {rfq.procurement_location}
                </h3>
              </div>

              <QuoteCard.Status />
            </QuoteCard.Header>
            <QuoteCard.Body>
              <div className="flex items-center gap-2">
                <BoxIcon className="size-3 text-blue-500" />

                <p>{rfq.goods_description}</p>
              </div>
            </QuoteCard.Body>
            <QuoteCard.Footer>
              <div className="flex items-baseline gap-1">
                <p className="text-sm font-semibold text-green-600">
                  {rfq.target_price}
                </p>
                <span className="text-[11px] text-gray-400">(Estimated)</span>
              </div>
              <QuoteCard.CreatedAt />
            </QuoteCard.Footer>
          </QuoteCard>
        ) : (
          <QuoteCard
            key={rfq.id || rfq.quotes_id}
            quote={rfq}
            onSelect={onSelect}
          >
            <QuoteCard.Header>
              <QuoteCard.FromTo />
              <QuoteCard.Status />
            </QuoteCard.Header>
            <QuoteCard.Body>
              <QuoteCard.FreightInfo />
              <QuoteCard.PickupDate />
            </QuoteCard.Body>
            <QuoteCard.Footer>
              <QuoteCard.Price />
              <QuoteCard.CreatedAt />
            </QuoteCard.Footer>
          </QuoteCard>
        ),
      )}
    </div>
  );
}

/* 💀 Skeleton Loader */
export function RFQSkeletonList() {
  return (
    <div className="space-y-3 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm"
        >
          <div className="flex justify-between mb-2">
            <div className="h-3 w-1/3 bg-gray-200 rounded"></div>
            <div className="h-3 w-12 bg-gray-200 rounded"></div>
          </div>
          <div className="h-3 w-2/3 bg-gray-200 rounded mb-2"></div>
          <div className="h-3 w-1/4 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  );
}

RFQs.Expanded = function RFQsExpanded({
  selectedQuoteId,
  onClose,
}: {
  selectedQuoteId: LogisticsRequestQuote["id"];
  onClose: () => void;
}) {
  const { data } = useGetRequestedLogisticsQuotesQuery();

  const selectedQuote = data?.find((q) => q.id === selectedQuoteId);

  if (!selectedQuote) return null;

  const {
    from_json,
    to_json,
    ai_response,
    freight_type,
    // loading_date,
    pickup_date,
    goods_category,
    weight,
    status,
    incoterm,
    created_at,
    booking_medium,
  } = selectedQuote;

  const quote = ai_response?.quote || {};
  const breakdown = quote.breakdown || {};

  const FreightIcon =
    freight_type?.toLowerCase() === "air" ? AirPlaneIcon : ShipIcon;

  const { label, className: statusColor } = getRFQStatusStyles(status);

  if (booking_medium === "procurement_request") {
    return (
      <div className="h-full overflow-y-auto p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
        {/* Back Button */}
        <button
          onClick={onClose}
          className="mb-4 text-sm text-blue-600 hover:underline"
        >
          ← Back to RFQs
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <MapPinIcon className="size-4 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-800">
                {selectedQuote?.procurement_location}
              </h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <BoxIcon className="size-4 text-blue-500" />
              <span>{selectedQuote?.goods_specifications}</span>
            </div>

            {created_at && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <ClockIcon className="size-4 text-gray-300" />
                <span>{timeAgo(parseServerDate(created_at))}</span>
              </div>
            )}
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${statusColor}`}
          >
            {label || "Pending"}
          </span>
        </div>

        <hr />

        <div className="space-y-[10px] mt-3">
          {selectedQuote &&
            OrderRequestKeys.map(({ label, key }) => {
              const value = selectedQuote?.[key] || "";

              const isFilled =
                value !== null && value !== undefined && value !== "";

              return (
                <p key={key} className="flex items-center gap-2 text-sm">
                  {isFilled ? (
                    <CheckIcon className="size-4 text-custom-blue-medium shrink-0" />
                  ) : (
                    <CrossIcon className="size-4 flex items-center justify-center text-red-500 shrink-0" />
                  )}

                  <span className="text-[#475467]">
                    {label}: {String(value)}
                  </span>
                </p>
              );
            })}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
      {/* Back Button */}
      <button
        onClick={onClose}
        className="mb-4 text-sm text-blue-600 hover:underline"
      >
        ← Back to RFQs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <MapPinIcon className="size-4 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {from_json?.name} → {to_json?.name}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FreightIcon className="size-4 text-blue-500" />
            <span>{freight_type?.toUpperCase()} Freight</span>
            <span className="text-gray-400">|</span>
            <span>{weight || "—"} kg</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
            {incoterm && (
              <span>
                <span className="font-medium text-gray-600">Incoterm:</span>{" "}
                <span className="italic">{incoterm.toUpperCase()}</span>
              </span>
            )}

            <span className="text-gray-300">•</span>

            {/* <span>
              <span className="font-medium text-gray-600">Loading:</span>{" "}
              {loading_date || "—"}
            </span>

            <span className="text-gray-300">•</span> */}

            <span>
              <span className="font-medium text-gray-600">Pickup:</span>{" "}
              {pickup_date || "—"}
            </span>
          </div>

          {created_at && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <ClockIcon className="size-4 text-gray-300" />
              <span>{timeAgo(parseServerDate(created_at))}</span>
            </div>
          )}
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${statusColor}`}
        >
          {label || "Pending"}
        </span>
      </div>

      {/* Quote Overview */}
      {quote?.estimated_price_usd ? (
        <div className="relative border border-green-100 bg-green-50 rounded-lg p-4 mb-5">
          <div className="absolute -top-2 -right-2 bg-green-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
            Estimate
          </div>
          <p className="text-base font-semibold text-green-700">
            {calculatePriceRange(quote.estimated_price_usd).formatted("$")}
          </p>
          <p className="text-xs text-green-600 mt-1">
            Valid for {quote.validity_days || "—"} days • Transit:{" "}
            {quote.estimated_transit_days || "—"} days
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-5">
          No quote available yet. Awaiting partner responses.
        </p>
      )}

      {/* Goods Info */}
      <div className="border-t border-gray-100 pt-4 mb-5">
        <h3 className="text-sm font-medium text-gray-700 mb-1">
          Goods Details
        </h3>
        <p className="text-sm text-gray-600">
          {goods_category || "Not specified"}
        </p>
      </div>

      {/* Cost Breakdown */}
      {Object.keys(breakdown).length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Cost Breakdown
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(breakdown).map(([key, val]) => (
              <div
                key={key}
                className="flex justify-between border-b border-gray-100 py-1"
              >
                <span className="capitalize text-gray-500">
                  {key.replaceAll("_", " ")}
                </span>
                <span className="font-medium text-gray-700">
                  ~ ${Number(val).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
